// Picks a readable text color for a customer-chosen background.

const INK = '#201e1d'
const WHITE = '#ffffff'

type Rgb = [number, number, number]

// Hex (#rgb, #rrggbb) and rgb()/rgba() are what the admin app saves;
// anything else is unknown and gets the safe default (white on it).
export function parseColor(input: string): Rgb | null {
  const s = input.trim()
  const hex = s.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)
  if (hex) {
    let h = hex[1]!
    if (h.length === 3) h = h.split('').map((c) => c + c).join('')
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]
  }
  const rgb = s.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i)
  if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])]
  return null
}

// WCAG relative luminance.
export function relativeLuminance([r, g, b]: Rgb): number {
  const lin = (c: number) => {
    const v = c / 255
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b)
}

function contrast(a: number, b: number): number {
  const [hi, lo] = a > b ? [a, b] : [b, a]
  return (hi + 0.05) / (lo + 0.05)
}

function toHex([r, g, b]: Rgb): string {
  return '#' + [r, g, b].map((c) => Math.round(c).toString(16).padStart(2, '0')).join('')
}

function mix(a: Rgb, b: Rgb, amount: number): Rgb {
  return [0, 1, 2].map((i) => a[i]! + (b[i]! - a[i]!) * amount) as Rgb
}

/**
 * A scale around the customer's colour, so the chrome that surrounds
 * their chat is theirs too: pale washes for hovers and rings, and a
 * darkened form that stays readable as text on white.
 */
/** A wash of the brand's own foreground, for chrome that sits on it. */
export function softOn(color: string): string | null {
  const rgb = parseColor(color)
  if (!rgb) return null
  const fg = parseColor(readableForeground(color))!
  return toHex(mix(rgb, fg, 0.16))
}

export function shades(color: string): { wash: string; ring: string; base: string; text: string } | null {
  const rgb = parseColor(color)
  if (!rgb) return null
  const white = parseColor(WHITE)!
  const ink = parseColor(INK)!
  // Darken until it reads as text on white; a pale brand colour needs
  // more of it than a deep one, and some never get there, hence the cap.
  let text = rgb
  for (let i = 0; i < 12 && contrast(relativeLuminance(text), 1) < 4.5; i++) {
    text = mix(text, ink, 0.12)
  }
  return {
    wash: toHex(mix(rgb, white, 0.88)),
    ring: toHex(mix(rgb, white, 0.7)),
    base: toHex(rgb),
    text: toHex(text),
  }
}

// Ink or white, whichever contrasts more with `background`.
export function readableForeground(background: string): string {
  const rgb = parseColor(background)
  if (!rgb) return WHITE
  const l = relativeLuminance(rgb)
  const inkL = relativeLuminance(parseColor(INK)!)
  return contrast(l, inkL) >= contrast(l, 1) ? INK : WHITE
}
